import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, euro, formatError, STATI_NOLEGGIO } from "@/lib/api";
import { SearchSelect } from "@/components/SearchSelect";

const pad = (n) => String(n).padStart(2, "0");
const dtLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const nuovo = () => { const s = new Date(); s.setMinutes(0, 0, 0); const e = new Date(s); e.setHours(e.getHours() + 24); return { inizio: dtLocal(s), fine: dtLocal(e) }; };
const EMPTY = { cliente_id: "", bici_ids: [], inizio: "", fine: "", tariffa_tipo: "giorno", cauzione: 0, documento: "", stato: "prenotato", note: "" };
const L = ({ children }) => <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;

const ORE_UNITA = { ora: 1, mezza: 6, giorno: 24 };
export const UNITA_LABEL = { ora: ["ora", "ore"], mezza: ["mezza giornata", "mezze giornate"], giorno: ["giorno", "giorni"], weekend: ["weekend", "weekend"] };
const unita = (f) => {
  const ore = (new Date(f.fine) - new Date(f.inizio)) / 36e5;
  if (!(ore > 0)) return 0;
  return ORE_UNITA[f.tariffa_tipo] ? Math.max(1, Math.ceil(ore / ORE_UNITA[f.tariffa_tipo] - 1e-9)) : 1;
};
const prossimoWeekend = (inizio) => {
  const d = inizio ? new Date(inizio) : new Date();
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
  d.setHours(9, 0, 0, 0);
  const e = new Date(d); e.setDate(e.getDate() + 1); e.setHours(19, 0, 0, 0);
  return { inizio: dtLocal(d), fine: dtLocal(e) };
};
const fuoriWeekend = (f) => f.inizio && f.fine && (new Date(f.inizio).getDay() !== 6 || ![0, 1].includes(new Date(f.fine).getDay()));
const prezzo = (b, tipo) => b[`tariffa_${tipo}`] || 0;

const SceltaBici = ({ flotta, occupate, sel, onToggle, tipo }) => (
  <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="noleggio-bici-list">
    {flotta.map((b) => {
      const off = b.stato_flotta !== "disponibile" || (occupate.includes(b.id) && !sel.includes(b.id));
      const motivo = b.stato_flotta !== "disponibile" ? "non disponibile" : occupate.includes(b.id) ? "già noleggiata nel periodo" : "";
      return (
        <label key={b.id} className={`flex items-center gap-3 rounded px-2 py-1.5 text-sm ${off ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
          <Checkbox checked={sel.includes(b.id)} disabled={off} onCheckedChange={() => onToggle(b.id)} data-testid={`noleggio-bici-${b.id}`} />
          <span className="font-mono text-xs font-bold text-orange-600">{b.codice}</span>
          <span className="flex-1">{b.marca} {b.modello} {b.taglia && <span className="text-xs text-slate-400">· {b.taglia}</span>}</span>
          <span className="font-mono text-xs text-slate-500">{motivo || euro(prezzo(b, tipo))}</span>
        </label>
      );
    })}
    {!flotta.length && <p className="py-4 text-center text-sm text-slate-500">Nessuna bici nella flotta. Aggiungile da "Flotta noleggio".</p>}
  </div>
);

export const NoleggioDialog = ({ open, onOpenChange, item, clienti, flotta, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [occupate, setOccupate] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(item?.id ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, item[k] ?? EMPTY[k]])) } : { ...EMPTY, ...nuovo(), ...(item || {}) });
  }, [open, item]);
  useEffect(() => {
    if (!open || !f.inizio || !f.fine || f.fine <= f.inizio) return;
    api.get("/noleggi/disponibilita", { params: { inizio: f.inizio, fine: f.fine, escludi: item?.id || "" } }).then((r) => setOccupate(r.data.occupate)).catch(() => {});
  }, [open, f.inizio, f.fine, item]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const scelte = flotta.filter((b) => f.bici_ids.includes(b.id));
  const toggle = (id) => {
    const ids = f.bici_ids.includes(id) ? f.bici_ids.filter((x) => x !== id) : [...f.bici_ids, id];
    setF((s) => ({ ...s, bici_ids: ids, cauzione: flotta.filter((b) => ids.includes(b.id)).reduce((a, b) => a + (b.cauzione || 0), 0) }));
  };
  const u = unita(f);
  const totale = scelte.reduce((a, b) => a + prezzo(b, f.tariffa_tipo), 0) * u;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.cliente_id) return toast.error("Seleziona un cliente");
    if (!f.bici_ids.length) return toast.error("Seleziona almeno una bici");
    setSaving(true);
    try {
      const payload = { ...f, cauzione: Number(f.cauzione) || 0 };
      const { data } = item?.id ? await api.put(`/noleggi/${item.id}`, payload) : await api.post("/noleggi", payload);
      toast.success(item?.id ? "Noleggio aggiornato" : `Noleggio ${data.numero} creato`);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="noleggio-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{item?.id ? `Noleggio ${item.numero}` : "Nuovo noleggio"}</DialogTitle>
          <DialogDescription>Cliente, periodo e bici della flotta.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><L>Cliente *</L>
            <SearchSelect testId="noleggio-cliente-select" value={f.cliente_id} placeholder="Cerca cliente..." onChange={(v) => set("cliente_id", v)}
              options={clienti.map((c) => ({ value: c.id, label: `${c.nome} ${c.cognome || ""}`.trim(), hint: c.telefono }))} />
          </div>
          <div><L>Inizio *</L><Input type="datetime-local" value={f.inizio} onChange={(e) => set("inizio", e.target.value)} data-testid="noleggio-inizio-input" /></div>
          <div><L>Fine prevista *</L><Input type="datetime-local" value={f.fine} onChange={(e) => set("fine", e.target.value)} data-testid="noleggio-fine-input" /></div>
          <div><L>Tariffa</L>
            <Select value={f.tariffa_tipo} onValueChange={(v) => setF((s) => ({ ...s, tariffa_tipo: v, ...(v === "weekend" ? prossimoWeekend(s.inizio) : {}) }))}>
              <SelectTrigger data-testid="noleggio-tariffa-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="giorno">Giornaliera</SelectItem><SelectItem value="mezza">Mezza giornata (fino a 6 ore)</SelectItem><SelectItem value="ora">Oraria</SelectItem><SelectItem value="weekend">Weekend (sabato–domenica)</SelectItem></SelectContent>
            </Select>
            {f.tariffa_tipo === "weekend" && fuoriWeekend(f) && <p className="mt-1 text-xs text-amber-700" data-testid="noleggio-weekend-avviso">Attenzione: il periodo non va da sabato a domenica.</p>}
          </div>
          <div><L>Stato</L>
            <Select value={f.stato} onValueChange={(v) => set("stato", v)}>
              <SelectTrigger data-testid="noleggio-stato-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATI_NOLEGGIO.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><L>Bici * ({f.bici_ids.length} selezionate)</L>
            <SceltaBici flotta={flotta} occupate={occupate} sel={f.bici_ids} onToggle={toggle} tipo={f.tariffa_tipo} />
          </div>
          <div><L>Cauzione (€)</L><Input type="number" step="0.01" min="0" value={f.cauzione} onChange={(e) => set("cauzione", e.target.value)} data-testid="noleggio-cauzione-input" /></div>
          <div><L>Documento cliente</L><Input placeholder="es. C.I. AB1234567" value={f.documento} onChange={(e) => set("documento", e.target.value)} data-testid="noleggio-documento-input" /></div>
          <div className="sm:col-span-2"><L>Note</L><Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="noleggio-note-input" /></div>
          <div className="flex items-end justify-between rounded-md bg-slate-900 p-4 text-white sm:col-span-2">
            <span className="text-xs text-slate-400">{u} {UNITA_LABEL[f.tariffa_tipo][u === 1 ? 0 : 1]} × {scelte.length} bici</span>
            <span className="font-mono text-2xl font-bold" data-testid="noleggio-totale">{euro(totale)}</span>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="noleggio-cancel-button">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="noleggio-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
